import {Component, Input, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';

import {SelectItem} from 'primeng/api';

import {EventService, PageRequest} from '../../../../core';

import {AddProjectMemberRequest, Project, ProjectAccess} from '../../../../core/models/project';
import {ProjectService} from '../../../../core/services/project.service';
import {UserService} from '../../../../core/services/user.service';
import {BreadcrumbService} from '../../../../breadcrumb.service';
import {User} from '../../../../core/models/user';
import {UserGroup} from '../../../../core/models';
import {UserGroupService} from '../../../../core/services';
import {ProjectSidebarService} from '../../../../projectsidebar.service';
import {TokenService} from '../../../../core/auth';


@Component({
  selector: 'app-project-members',
  templateUrl: './project-members.page.html',
  styleUrls: ['./project-members.page.scss']
})
export class ProjectMembersPage implements OnInit {
  @Input() project: Project;

  projectAccessUsers: ProjectAccess[];
  projectAccessUserGroups: ProjectAccess[];
  roles: SelectItem[];
  role: SelectItem;

  loading = false;
  id: string;

  viewMembers: boolean = true;

  authUser = this.token.whoAmI();
  userProjectAccess: ProjectAccess;

  @Input() userQuery = new PageRequest(1, 1000);

  selectedUser: User;
  userList: SelectItem[] = [];
  selectedGroup: UserGroup;
  groupList: SelectItem[] = [];
  originalGroups: UserGroup [] = [];

  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private token: TokenService,
    private userGroupService: UserGroupService,
    private userService: UserService,
    private projectSidebarService: ProjectSidebarService,
    private eventService: EventService,
    private breadcrumbService: BreadcrumbService
  ) {
  }

  ngOnInit() {
    this.loading = true;
    this.route.parent.params.subscribe((params) => {
      this.id = params.id;
    });

    this.roles = [
      {label: 'Project Access', value: 'PROJECT_ACCESS'}];
    this.route.parent.data.subscribe(
      (data) => {
        if (data.project) {
          this.project = data.project;
          this.userProjectAccess = Object.assign({}, data.userProjectAccess);
          this.breadcrumbService.setItems([
            {label: 'projects', routerLink: ['/project', 'list']},
            {label: this.project.name},
            {label: 'members',},
          ]);

          this.loadAccess(this.project.id, true);
          this.projectSidebarService.setProject(this.project);
        }
        this.loading = false;
      }
    );

  }

  populateUserDropdown(){
    this.userService.search(this.userQuery).subscribe(
      (data) => {
        data.content.forEach((user) => {
          this.addUserToDropdown(user);
        });
        delete (this.loading);
      },
      (error) => {
        delete (this.loading);
      }
    );
  }

  populateGroupDropdown(){
    this.userGroupService.search(this.userQuery).subscribe(
      (data) => {
        data.content.forEach((userGroup) => {
          this.originalGroups.push(userGroup);
          this.addGroupToDropdown(userGroup);
        });
        delete (this.loading);
      },
      (error) => {
        delete (this.loading);
      }
    );
  }

  addUserToDropdown(user: User) {
    let addUser = true;
    this.projectAccessUsers.forEach((item) => {
      if(item.user.id === user.id){
        addUser = false;
      }
    })
    if(addUser){
      this.userList.push({label: user.name, value: user});
    }
  }

  addGroupToDropdown(userGroup: UserGroup){
    let addGroup = true;
    this.projectAccessUserGroups.forEach((item) => {
      if(item.userGroup.id === userGroup.id){
        addGroup = false;
      }
    })
    if(addGroup){
      this.groupList.push({label: userGroup.name, value: userGroup});
    }
  }

  updateDropdown(access: ProjectAccess){
    if(access !== null){
      if(access.user) {
          this.userList.push({label: access.user.name, value: access.user});
          this.userList.sort((a, b) => a.label == b.label ? 0 : a.label < b.label ? -1 : 1);
      } else{
          this.groupList.push({label: access.userGroup.name, value: access.userGroup});
          this.groupList.sort((a, b) => a.label == b.label ? 0 : a.label < b.label ? -1 : 1);
      }
    }
  }

  loadAccess(id: string, firstLoad: boolean) {
    this.projectService.getAccessUsers(id).subscribe(
      (data) => {
        this.projectAccessUsers = data;
        if(firstLoad){
          this.populateUserDropdown();
        }
      }
    );

    this.projectService.getAccessUserGroups(id).subscribe(
      (data) => {
        this.projectAccessUserGroups = data;
        if(firstLoad){
          this.populateGroupDropdown();
        }
        
      }
    );
  }

  addMember(role) {
    if (!this.selectedUser) {
      this.eventService.warn('Member is required');
      return;

    }
    if (!role) {
      this.eventService.warn('Role is required');
      return;
    }
    const addProjectMemberRequest = new AddProjectMemberRequest(this.project.id, this.selectedUser.id, null, role);
    this.projectService.createAccess(this.project.id, addProjectMemberRequest).subscribe(
      (data) => {
        this.projectAccessUsers.push(data);
        this.role = null;
        this.selectedUser = null;
        this.removeUserFromDropdown(data.user.id);
      }
    );
  }

  addGroup(role) {
    if (!this.selectedGroup) {
      this.eventService.warn('Group is required');
      return;

    }
    if (!role) {
      this.eventService.warn('Role is required');
      return;
    }
    const addProjectMemberRequest = new AddProjectMemberRequest(this.project.id, null, this.selectedGroup.id, role);
    this.projectService.createAccess(this.project.id, addProjectMemberRequest).subscribe(
      (data) => {
        this.projectAccessUserGroups.push(data);
        this.role = null;
        this.selectedGroup = null;
        this.removeGroupFromDropdown(data.userGroup.id);
      }
    );
  }

  isSuperUser(){
    return this.authUser.currentCustomer.admin;
  }

  findValue(userGroup: UserGroup){
    let showLink = true;
    if(this.isSuperUser()){
      showLink = false;
      this.originalGroups.forEach((item) => {
        if(item.id === userGroup.id){
          showLink = true;
        }
      });
    }
    return showLink;
  }

  removeUserFromDropdown(userId: string):void{
    this.userList.forEach((item, index) => {
      if (item.value.id === userId)
        this.userList.splice(index, 1);
    });
  }

  removeGroupFromDropdown(groupId: string):void{
    this.groupList.forEach((item, index) => {
      if (item.value.id === groupId)
        this.groupList.splice(index, 1);
    });
  }

  delete(access: ProjectAccess) {
    this.projectService.removeAccess(access.id).subscribe(
      (data) => {
        this.updateDropdown(access);
        this.loadAccess(this.project.id, false);

      }
    );
  }

  toggleViewMembers() {
    this.viewMembers = !this.viewMembers;
  }

}
